// ---------------------------------------------------------------------------
// Ordered schema migrations, keyed off meta.schema_version.
//
// Rules:
//  - SCHEMA_SQL (schema.ts) is the CREATE-IF-NOT-EXISTS shape for FRESH
//    databases and runs on every open; new tables can simply be added there.
//  - Changes to EXISTING tables (ALTER TABLE, data rewrites) go here, in an
//    ordered migration that runs exactly once per database.
//  - A fresh database gets meta.schema_version = SCHEMA_VERSION and skips all
//    migrations; an old database walks forward one step at a time.
// ---------------------------------------------------------------------------

import type { Database as SqlJsDatabase } from 'sql.js';
import { SCHEMA_VERSION } from './schema';
import { PROMPTS, PROMPT_VERSION } from '../ai/prompts';
import { bumpVersion } from '../utils/version';

interface Migration {
  /** The schema version this migration upgrades the database TO. */
  to: number;
  up: (db: SqlJsDatabase) => void;
}

function hasColumn(db: SqlJsDatabase, table: string, column: string): boolean {
  const res = db.exec(`PRAGMA table_info(${table})`);
  if (!res.length) return false;
  const nameIdx = res[0].columns.indexOf('name');
  return res[0].values.some((row) => row[nameIdx] === column);
}

const MIGRATIONS: Migration[] = [
  {
    to: 2,
    up: (db) => {
      // Interview timeline: per-program interview date.
      if (!hasColumn(db, 'dental_program', 'interview_date')) {
        db.run('ALTER TABLE dental_program ADD COLUMN interview_date TEXT');
      }

      // Refresh the prompt templates to the v1.1 defaults. Safe to overwrite:
      // in every pre-v2 build the provider ignored DB templates entirely (it
      // only read the static PROMPTS), so stored edits were never in effect.
      const now = new Date().toISOString();
      for (const [key, def] of Object.entries(PROMPTS)) {
        db.run(
          `INSERT OR REPLACE INTO prompt_template (id, agent, version, label, template, updated_at)
           VALUES (?, ?, ?, ?, ?, ?)`,
          [`tpl_${key}`, def.agent, PROMPT_VERSION, def.label, def.template, now],
        );
      }

      // Settings cleanup: drop removed keys, clear developer-machine defaults,
      // and move the prompt version forward with the refreshed templates.
      const row = db.exec('SELECT data FROM app_settings WHERE id = 1');
      if (row.length && row[0].values.length) {
        try {
          const settings = JSON.parse(String(row[0].values[0][0])) as Record<string, unknown>;
          delete settings.webSearchMode;
          delete settings.whisperModelPath;
          delete settings.encryptLocalData;
          if (settings.promptVersion === 'prompts-v1.0') settings.promptVersion = PROMPT_VERSION;
          db.run('INSERT OR REPLACE INTO app_settings (id, data) VALUES (1, ?)', [
            JSON.stringify(settings),
          ]);
        } catch {
          // Unparseable settings row: leave it; getSettings() falls back to defaults.
        }
      }
    },
  },
  {
    to: 3,
    up: (db) => {
      // Refresh the built-in prompt templates to the v1.2 defaults (exact
      // output contracts: allowed categories, quality/role enums, counts).
      // ONLY rows the user never touched are replaced: seeded rows still carry
      // version 'prompts-v1.1', while saving an edit in Prompts & Rubric
      // stamps a bumped version — those keep the user's text.
      const now = new Date().toISOString();
      for (const [key, def] of Object.entries(PROMPTS)) {
        db.run(
          `UPDATE prompt_template SET version = ?, label = ?, template = ?, updated_at = ?
           WHERE id = ? AND version = 'prompts-v1.1'`,
          [PROMPT_VERSION, def.label, def.template, now, `tpl_${key}`],
        );
      }

      // Move the active prompt version forward so the score cache re-grades
      // fresh answers against the new prompts instead of matching old grades.
      const row = db.exec('SELECT data FROM app_settings WHERE id = 1');
      if (row.length && row[0].values.length) {
        try {
          const settings = JSON.parse(String(row[0].values[0][0])) as Record<string, unknown>;
          const current = typeof settings.promptVersion === 'string' ? settings.promptVersion : '';
          settings.promptVersion =
            !current || current === 'prompts-v1.1' ? PROMPT_VERSION : bumpVersion(current);
          db.run('INSERT OR REPLACE INTO app_settings (id, data) VALUES (1, ?)', [
            JSON.stringify(settings),
          ]);
        } catch {
          // Unparseable settings row: leave it; getSettings() falls back to defaults.
        }
      }
    },
  },
  {
    to: 4,
    up: () => {
      // profile_document is a NEW table, created by SCHEMA_SQL (which runs on
      // every open) — nothing to alter. The version bump only records that old
      // databases have walked past this point.
    },
  },
  {
    to: 5,
    up: (db) => {
      // Research review workflow. Existing questions were curated implicitly by
      // being kept, so they default to 'accepted'; only NEW automated research
      // lands as 'needs_review'.
      if (!hasColumn(db, 'interview_question', 'review_status')) {
        db.run("ALTER TABLE interview_question ADD COLUMN review_status TEXT NOT NULL DEFAULT 'accepted'");
      }
      if (!hasColumn(db, 'interview_question', 'reviewed_at')) {
        db.run('ALTER TABLE interview_question ADD COLUMN reviewed_at TEXT');
      }
      if (!hasColumn(db, 'interview_question', 'review_notes')) {
        db.run("ALTER TABLE interview_question ADD COLUMN review_notes TEXT NOT NULL DEFAULT ''");
      }

      // Mock interview resume: remember which question the candidate was on.
      if (!hasColumn(db, 'mock_interview', 'current_step')) {
        db.run('ALTER TABLE mock_interview ADD COLUMN current_step INTEGER NOT NULL DEFAULT 0');
      }

      // improvement_plan / saved_answer_version / prompt_template_history are
      // NEW tables created by SCHEMA_SQL — nothing to alter here.

      // Refresh untouched built-in prompt templates to the v1.3 defaults
      // (school-fit source metadata). Same rule as the v3 migration: only rows
      // still carrying the seeded 'prompts-v1.2' version are replaced; edited
      // templates (bumped versions) keep the user's text.
      const now = new Date().toISOString();
      for (const [key, def] of Object.entries(PROMPTS)) {
        db.run(
          `UPDATE prompt_template SET version = ?, label = ?, template = ?, updated_at = ?
           WHERE id = ? AND version = 'prompts-v1.2'`,
          [PROMPT_VERSION, def.label, def.template, now, `tpl_${key}`],
        );
      }

      // Move the active prompt version forward so the score cache re-grades
      // fresh answers against the new prompts.
      const row = db.exec('SELECT data FROM app_settings WHERE id = 1');
      if (row.length && row[0].values.length) {
        try {
          const settings = JSON.parse(String(row[0].values[0][0])) as Record<string, unknown>;
          const current = typeof settings.promptVersion === 'string' ? settings.promptVersion : '';
          settings.promptVersion =
            !current || current === 'prompts-v1.2' ? PROMPT_VERSION : bumpVersion(current);
          db.run('INSERT OR REPLACE INTO app_settings (id, data) VALUES (1, ?)', [
            JSON.stringify(settings),
          ]);
        } catch {
          // Unparseable settings row: leave it; getSettings() falls back to defaults.
        }
      }
    },
  },
  {
    to: 6,
    up: (db) => {
      // Refresh untouched built-in prompt templates to the v1.4 defaults:
      // realistic conversational grading, conditional school-fit expectations,
      // and explicit post-2025 DAT scale guidance. Edited templates keep the
      // user's text and get an app-settings version bump below.
      const now = new Date().toISOString();
      for (const [key, def] of Object.entries(PROMPTS)) {
        db.run(
          `UPDATE prompt_template SET version = ?, label = ?, template = ?, updated_at = ?
           WHERE id = ? AND version = 'prompts-v1.3'`,
          [PROMPT_VERSION, def.label, def.template, now, `tpl_${key}`],
        );
      }

      // Move the active prompt version forward so cached strict grades are not
      // reused after the grading calibration changes.
      const row = db.exec('SELECT data FROM app_settings WHERE id = 1');
      if (row.length && row[0].values.length) {
        try {
          const settings = JSON.parse(String(row[0].values[0][0])) as Record<string, unknown>;
          const current = typeof settings.promptVersion === 'string' ? settings.promptVersion : '';
          settings.promptVersion =
            !current || current === 'prompts-v1.3' ? PROMPT_VERSION : bumpVersion(current);
          db.run('INSERT OR REPLACE INTO app_settings (id, data) VALUES (1, ?)', [
            JSON.stringify(settings),
          ]);
        } catch {
          // Unparseable settings row: leave it; getSettings() falls back to defaults.
        }
      }
    },
  },
  {
    to: 7,
    up: (db) => {
      // attempt_video / program_dossier / red_flag_plan are NEW tables created
      // by SCHEMA_SQL, and the new dossier/redflag prompt templates are seeded
      // per-template by seedDatabaseIfNeeded — nothing structural to alter.

      // Refresh untouched built-in prompt templates to the v1.5 defaults (the
      // research template gained a {{categoryFocus}} placeholder). Same rule as
      // earlier migrations: only rows still carrying the seeded 'prompts-v1.4'
      // version are replaced; edited templates keep the user's text.
      const now = new Date().toISOString();
      for (const [key, def] of Object.entries(PROMPTS)) {
        db.run(
          `UPDATE prompt_template SET version = ?, label = ?, template = ?, updated_at = ?
           WHERE id = ? AND version = 'prompts-v1.4'`,
          [PROMPT_VERSION, def.label, def.template, now, `tpl_${key}`],
        );
      }

      // Move the active prompt version forward so the score cache re-grades
      // fresh answers against the new prompts.
      const row = db.exec('SELECT data FROM app_settings WHERE id = 1');
      if (row.length && row[0].values.length) {
        try {
          const settings = JSON.parse(String(row[0].values[0][0])) as Record<string, unknown>;
          const current = typeof settings.promptVersion === 'string' ? settings.promptVersion : '';
          settings.promptVersion =
            !current || current === 'prompts-v1.4' ? PROMPT_VERSION : bumpVersion(current);
          db.run('INSERT OR REPLACE INTO app_settings (id, data) VALUES (1, ?)', [
            JSON.stringify(settings),
          ]);
        } catch {
          // Unparseable settings row: leave it; getSettings() falls back to defaults.
        }
      }
    },
  },
  {
    to: 8,
    up: () => {
      // Cloud-sync tables (sync_meta/sync_control/sync_outbox/sync_doc_state/
      // sync_conflict) and the per-table outbox triggers are all created by
      // SCHEMA_SQL (which runs on every open) with IF NOT EXISTS — nothing to
      // alter here. The triggers stay dormant until a cloud account is linked
      // (sync_control.enabled flips to '1'), so existing offline databases are
      // unaffected until the user opts in.
    },
  },
  {
    to: 9,
    up: () => {
      // sync_pending_blob is a NEW table created by SCHEMA_SQL (IF NOT EXISTS
      // on every open) — nothing to alter. The version bump only records that
      // old databases have walked past this point.
    },
  },
];

export function getSchemaVersion(db: SqlJsDatabase): number | null {
  const res = db.exec("SELECT value FROM meta WHERE key = 'schema_version'");
  if (!res.length || !res[0].values.length) return null;
  const v = Number(res[0].values[0][0]);
  return Number.isFinite(v) ? v : null;
}

function setSchemaVersion(db: SqlJsDatabase, version: number): void {
  db.run("INSERT OR REPLACE INTO meta (key, value) VALUES ('schema_version', ?)", [
    String(version),
  ]);
}

/**
 * Bring a database to SCHEMA_VERSION. Throws if the database is FROM A NEWER
 * app version (downgrades are not supported — better to refuse than corrupt).
 */
export function runMigrations(db: SqlJsDatabase): void {
  let current = getSchemaVersion(db);
  if (current === null) {
    // Fresh database: SCHEMA_SQL already created the latest shape.
    setSchemaVersion(db, SCHEMA_VERSION);
    return;
  }
  if (current > SCHEMA_VERSION) {
    throw new Error(
      `This database is from a newer version of the app (schema v${current}, app supports v${SCHEMA_VERSION}). ` +
        'Update the app instead of importing this file.',
    );
  }
  for (const migration of MIGRATIONS) {
    if (migration.to > current) {
      migration.up(db);
      setSchemaVersion(db, migration.to);
      current = migration.to;
    }
  }
}
